import { useCallback, useEffect, useState } from "react";
import { apiPcf, type PcpLinha } from "../lib/api";
import { useTheme } from "../hooks/useTheme";
import { Logo } from "../components/Logo";
import { logout, type Usuario } from "../lib/auth";
import { MsgModal } from "../components/MsgModal";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";
import { AntecipadosAlerta } from "../components/AntecipadosAlerta";

// atcadpcf / TELA-1 "Planejamento de Produção" — hub do módulo PCF.
// Lista os itens a produzir (pedpro PCP), filtra por período de entrega + cliente
// + produto, e dá acesso a Produzir / Nova PCF (Demanda/Complementar) / Duplicar /
// Retirar da Listagem. Fiel à TELA-1.prc (MOSTRAR-TABELA, CARREGAR-CLIENTE/PRODUTO).

const fmtQtd = (n: number) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    n || 0
  );

interface Props {
  usuario: Usuario;
  onVoltar: () => void;
  onProduzir: (linha: PcpLinha) => void;
  onNovaDemanda: () => void;
  onNovaComplementar: () => void;
  onDuplicar: () => void;
  onAbrirPcf?: (numpcf: number) => void; // alerta de faturamento antecipado: abrir a PCF para programar
  naoProgramadas?: boolean; // painel card 52: PARAMETRO NAO_PROGRAMADAS (TELA-1 L450, L1018-1021)
}

export function Planejamento({
  usuario,
  onVoltar,
  onProduzir,
  onNovaDemanda,
  onNovaComplementar,
  onDuplicar,
  onAbrirPcf,
  naoProgramadas = false,
}: Props) {
  const { theme, toggle } = useTheme();
  const [ini, setIni] = useState("");
  const [fim, setFim] = useState("");
  const [clientes, setClientes] = useState<string[]>([]);
  const [produtos, setProdutos] = useState<string[]>([]);
  const [cliente, setCliente] = useState("Todos");
  const [produto, setProduto] = useState("Todos");
  const [linhas, setLinhas] = useState<PcpLinha[] | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const ord = useOrdenacao(linhas, {
    cli: (l) => l.cliente, ped: (l) => l.numped, pro: (l) => l.produto, qtd: (l) => l.qtd, ent: (l) => l.dataEntrega,
  });
  const [confirmarRetirar, setConfirmarRetirar] = useState(false);
  const [nomeBd, setNomeBd] = useState("");
  const [erpUrl, setErpUrl] = useState("");
  // TELA-1 Opened L1029-1033: base ACOSNOGUEIRA desliga Produzir, Complementar, Nova e Duplicar.
  const travado = nomeBd.toUpperCase() === "ACOSNOGUEIRA";

  const cliParam = cliente === "Todos" ? null : cliente;
  const proParam = produto === "Todos" ? null : produto;

  const pesquisar = useCallback(
    async (di = ini, df = fim, cl = cliParam, pr = proParam) => {
      if (!di || !df) return;
      try {
        setErro(null);
        setCarregando(true);
        setSel(null);
        setConfirmarRetirar(false);
        const r = await apiPcf.listar(di, df, cl, pr);
        if (r.length >= 2000) setErro("Quantidade de Itens é superior ao máximo permitido."); // L943-947
        setLinhas(r);
      } catch (e) {
        setErro((e as Error).message);
      } finally {
        setCarregando(false);
      }
    },
    [ini, fim, cliParam, proParam]
  );

  // Boot: período default (min/max datent dos itens PCP) -> combos -> lista.
  useEffect(() => {
    void (async () => {
      try {
        const p = await apiPcf.periodo();
        const di = p.ini ?? "1900-01-01";
        const df = p.fim ?? "2999-12-31";
        setIni(di);
        setFim(df);
        setNomeBd(p.nomeBd ?? "");
        setErpUrl(p.erpUrl ?? "");
        const [cs, ps] = await Promise.all([
          apiPcf.clientes(di, df),
          apiPcf.produtos(di, df, null),
        ]);
        setClientes(cs);
        setProdutos(ps);
        await pesquisar(di, df, null, null);
      } catch (e) {
        setErro((e as Error).message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cliente muda -> recarrega o combo de produtos (fiel a CARREGAR-PRODUTO no SelChange).
  async function mudarCliente(v: string) {
    setCliente(v);
    setProduto("Todos");
    try {
      setProdutos(await apiPcf.produtos(ini, fim, v === "Todos" ? null : v));
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  function limpar() {
    setCliente("Todos");
    setProduto("Todos");
  }

  async function retirarConfirmado() {
    if (sel == null || !linhas) return;
    const l = linhas[sel];
    if (!l) return;
    try {
      await apiPcf.retirar(l.numped, l.sequen);
      await pesquisar();
    } catch (e) {
      setErro((e as Error).message);
      setConfirmarRetirar(false);
    }
  }

  const selecionado = sel != null && linhas ? linhas[sel] : null;

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <Logo />
          <h1>
            Planejamento de Produção
            <small>PCP — Planejamento e Controle de Produção{naoProgramadas ? " · PCFs não programadas" : ""}</small>
          </h1>
        </div>
        <span className="spacer" />
        <div className="user-badge" title={`Usuário: ${usuario.nomusu}`}>
          <span className="user-avatar">{usuario.apelid.charAt(0).toUpperCase()}</span>
          <span className="user-nome">{usuario.nomusu.split(" ")[0]}</span>
        </div>
        <button className="btn-secundario btn-voltar-painel" onClick={onVoltar} title="Voltar ao Painel">
          ← Painel
        </button>
        <button
          className="btn-secundario"
          onClick={toggle}
          title={theme === "light" ? "Modo escuro" : "Modo claro"}
        >{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
        <button className="btn-secundario" onClick={logout} title="Sair">Sair</button>
      </header>

      <div className="plan-filtros">
        <label className="plan-campo plan-campo-datas">
          <span>Data de Entrega</span>
          <div className="plan-datas">
            <input
              type="date"
              value={ini}
              onChange={(e) => setIni(e.target.value)}
              onBlur={(e) => {
                // T1-DATINI-WS LostFocus (L554-565)
                if (e.target.validity.badInput) setErro("Data Inicial do Período Inválida!");
              }}
            />
            <span>a</span>
            <input
              type="date"
              value={fim}
              onChange={(e) => setFim(e.target.value)}
              onBlur={(e) => {
                // T1-DATFIM-WS LostFocus (L524-542): vazia = data inicial; inválida avisa; e pesquisa.
                if (e.target.validity.badInput) {
                  setErro("Data Final do Período Inválida!");
                  return;
                }
                const df = fim || ini;
                if (!fim) setFim(df);
                void pesquisar(ini, df);
              }}
            />
          </div>
        </label>
        <label className="plan-campo">
          <span>Cliente</span>
          <select value={cliente} onChange={(e) => void mudarCliente(e.target.value)}>
            <option>Todos</option>
            {clientes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="plan-campo">
          <span>Produto</span>
          <select value={produto} onChange={(e) => setProduto(e.target.value)}>
            <option>Todos</option>
            {produtos.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <button className="btn-acao" onClick={() => void pesquisar()}>
          🔍 Pesquisar
        </button>
        <button className="btn-secundario" onClick={limpar}>
          🧹 Limpar
        </button>
      </div>

      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />

      <AntecipadosAlerta onAbrirPcf={onAbrirPcf} />

      <div className="tabela-wrap">
        <table className="tabela-pcfs">
          <thead>
            <tr>
              <ThOrdena ord={ord} col="cli">Cliente</ThOrdena>
              <ThOrdena ord={ord} col="ped">Pedido</ThOrdena>
              <ThOrdena ord={ord} col="pro">Produto</ThOrdena>
              <ThOrdena ord={ord} col="qtd" className="num">Quantidade</ThOrdena>
              <ThOrdena ord={ord} col="ent">Previsão de Entrega</ThOrdena>
            </tr>
          </thead>
          <tbody>
            {carregando && (
              <tr>
                <td colSpan={5} className="plan-vazio">
                  Carregando…
                </td>
              </tr>
            )}
            {!carregando && linhas && linhas.length === 0 && (
              <tr>
                <td colSpan={5} className="plan-vazio">
                  Nenhum item para produzir no filtro.
                </td>
              </tr>
            )}
            {!carregando &&
              linhas &&
              ord.linhas.map((l) => { const i = ord.idx(l); return (
                <tr
                  key={`${l.numped}-${l.sequen}-${l.codpro}-${i}`}
                  className={"row-clickable" + (sel === i ? " plan-sel" : "")}
                  onClick={() => {
                    if (naoProgramadas) return; // L450: no modo não programadas a linha não seleciona
                    setSel(i);
                    setConfirmarRetirar(false);
                  }}
                >
                  <td>{l.cliente}</td>
                  <td>
                    {/* TABELA-Click coluna 2 (L497-499): abre o pedido (ATCADPED) */}
                    {erpUrl && l.numped ? (
                      <a
                        href={`${erpUrl}/?go=ATCADPED&numped=${l.numped}`}
                        target="_blank"
                        rel="noopener"
                        onClick={(e) => e.stopPropagation()}
                        title="Abrir o pedido no sistema"
                      >
                        {l.numped}
                      </a>
                    ) : (
                      l.numped
                    )}
                  </td>
                  <td>{l.produto}</td>
                  <td className="num">{fmtQtd(l.qtd)}</td>
                  <td>{l.dataEntrega}</td>
                </tr>
              ); })}
          </tbody>
        </table>
      </div>

      {confirmarRetirar && selecionado && (
        <div className="plan-confirm">
          <span>
            Retirar da listagem o pedido <b>{selecionado.numped}</b> (seq {selecionado.sequen}) —{" "}
            {selecionado.produto}?
          </span>
          <button className="btn-acao plan-del" onClick={() => void retirarConfirmado()}>
            Confirmar
          </button>
          <button className="btn-secundario" onClick={() => setConfirmarRetirar(false)}>
            Cancelar
          </button>
        </div>
      )}

      <div className="plan-acoes">
        <button
          className="btn-acao"
          disabled={!selecionado || travado || naoProgramadas}
          onClick={() => selecionado && onProduzir(selecionado)}
        >
          ⚙ Produzir
        </button>
        <button className="btn-secundario" disabled={travado} onClick={onNovaComplementar}>
          ＋ Nova PCF Complementar
        </button>
        <button className="btn-secundario" disabled={travado || naoProgramadas} onClick={onNovaDemanda}>
          ＋ Nova PCF por Demanda
        </button>
        <button className="btn-secundario" disabled={travado} onClick={onDuplicar}>
          ⧉ Duplicar PCF
        </button>
        <span className="spacer" />
        <button
          className="btn-secundario plan-del"
          disabled={!selecionado}
          onClick={() => setConfirmarRetirar(true)}
        >
          ✕ Retirar da Listagem
        </button>
      </div>
    </div>
  );
}
